<template>
  <div
    ref="el"
    :class="{ ownPositionBG: own }"
    :style="{
      order: position,
      cursor: props.position > (props.selectedJackpotYear === 2026 ? 100 : 30) ? undefined : 'pointer'
    }"
    class="leaderboardItem"
    @click="ownPositionClickHandler"
  >
    <p class="address">
      <span style="width: 45px">{{ position }}</span>
      <el-avatar :size="30" :src="userScores?.avatarUrl" class="avatar-primary" />
      <span :class="{ ownPositionRow: own, 'ru-locale': isUserNameRuLocale }" class="user-name">
        <span>{{ userName }}</span>
      </span>
    </p>

    <div :class="{ isJackpot: isJackpot }" class="score">
      <div v-if="isJackpot" class="season-points">
        <img alt="" src="/public/img/icons/jackpotPoints.svg" width="24" />
        <span>{{ jackpotPoints }}</span>
      </div>

      <div v-if="userScores?.rewards && !isJackpot" class="rewards">
        <img alt="" class="chest" src="/public/img/icons/chest.svg" />

        <div v-for="(reward, index) in [...userScores.rewards].reverse()" :key="index" class="reward-item">
          <img
            :src="tokens[tokenMetaData(blockchain.contracts, reward.tokenAddress)?.name.contract as Tokens].toString()"
          />

          <span>{{ reward.amount }}</span>
        </div>
      </div>

      <div v-if="($device.isMobile || isJackpot) && BigNumber(checkedHistoricalSeason!).isNaN()" class="prize">
        <img
          v-if="pearlAmount"
          alt=""
          class="chest"
          src="/public/img/icons/jackpotPrize.svg"
          style="margin-left: 15px"
          width="24"
        />
        <div v-if="isJackpot && pearlAmount" class="pearl-reward">
          <span>≈ {{ pearlAmount }} usdt</span>
        </div>
        <span
          v-if="isJackpot && !pearlAmount && (selectedJackpotYear === 2026 ? position > 100 : position > 30)"
          class="no-rewards-text"
        >
          {{ $t('coreNoRewards') }}
        </span>
      </div>

      <div v-if="!isJackpot || !BigNumber(checkedHistoricalSeason!).isNaN()" class="season-points">
        <img :src="tokens.seasonPoints" alt="" width="24" />
        <span>{{ score }}</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';
import { BigNumber } from 'bignumber.js';
import useEnvs from '~/composables/useEnvs';
import tokens from '~/public/img/tokens';
import type { SeasonsUserScores } from '~/types/season';
import { tokenMetaData } from '~/utils';
import type { Tokens } from '~/utils/constants';
import { USER_NAME_MAX_LENGTH } from '~/utils/constants/profile';

const { blockchain } = useEnvs();
const { address: walletAddress } = useWeb3ModalAccount();

const el = ref<HTMLElement | null>(null);
const currentScrollTop = ref(0);
const currentScrollDirection = ref('');
const intersectionObserver = ref<IntersectionObserver>();

const props = defineProps<{
  score: number;
  position: number;
  own: boolean;
  userScores?: SeasonsUserScores;
  leaderboardLength: number;
  isJackpot: boolean;
  checkedHistoricalSeason?: number;
  jackpotScoreInYear?: number;
  currentSeasonId?: number;
  pearlAmount?: string;
  selectedJackpotYear?: number;
}>();

const emit = defineEmits<{
  changeShowStickyBgTop: [isShowStickyBgTop: boolean];
  changeShowStickyBgBottom: [isShowStickyBgBottom: boolean];
  jackpotItemClick: [position: number];
}>();

const userName = computed(() => {
  const address = props.userScores?.address;
  const userName = props.userScores?.name;

  if (address && userName && props.userScores.address && userName.length > USER_NAME_MAX_LENGTH) {
    return formatAddress(address);
  }

  return userName;
});

const isUserNameRuLocale = computed(() => props.userScores?.name && /^[А-Яа-я]/.test(props.userScores.name));

const jackpotPoints = computed(() => {
  // This hardcoded logic for seasons that duration is 7 days
  const MIN_SEASON_INDEX_FOR_SPECIAL_LOGIC = 5;
  const SPECIAL_SEASON_POINTS_DIVISOR = 4;

  const baseSeasonPoints = convertSeasonPlaceToJackpotScore(props.position);
  const jackpotScoreInYear = BigNumber(props.jackpotScoreInYear || 0).toNumber();

  const hasSpecialSeasonLogic = props?.currentSeasonId && props.currentSeasonId > MIN_SEASON_INDEX_FOR_SPECIAL_LOGIC;

  if (hasSpecialSeasonLogic) {
    return jackpotScoreInYear || Math.floor(baseSeasonPoints / SPECIAL_SEASON_POINTS_DIVISOR);
  }

  return jackpotScoreInYear || baseSeasonPoints;
});

// const formattedPearlAmount = computed(() => {
//   if (!props.pearlAmount) return '';

//   const num = Number(props.pearlAmount);
//   if (isNaN(num)) return props.pearlAmount;

//   return Math.floor(num).toString();
// });

defineExpose({ el });

const ownPositionClickHandler = () => {
  if (props.isJackpot) {
    const maxPosition = props.selectedJackpotYear === 2026 ? 100 : 30;
    if (props.position > maxPosition) return;

    emit('jackpotItemClick', props.position);
    return;
  }
  if (!props.own) return;
  const { isMobile } = useDevice();
  if (isMobile && el.value) {
    const scrollContainer = el.value?.parentNode?.parentNode as HTMLElement;
    if (scrollContainer) {
      const headerHeight = 85; // высота хедера на мобильных
      const elementTop = el.value.offsetTop;
      scrollContainer.scrollTo({
        top: elementTop - headerHeight,
        behavior: 'smooth'
      });
    }
  } else {
    el.value?.nextElementSibling?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
};

watch(
  [walletAddress, () => props.own, el],
  () => {
    intersectionObserver.value?.disconnect();
    emit('changeShowStickyBgBottom', false);
    emit('changeShowStickyBgTop', false);

    const actualElement =
      props.position < props.leaderboardLength
        ? (el.value?.nextElementSibling as HTMLElement)
        : (el.value?.previousElementSibling as HTMLElement);
    const parentScrollContainer = actualElement?.parentNode?.parentNode as HTMLElement;

    if (actualElement && props.own) {
      intersectionObserver.value = new IntersectionObserver(
        (entries) => {
          if (parentScrollContainer.scrollTop === 0) {
            currentScrollDirection.value = '';
          } else if (currentScrollTop.value > parentScrollContainer.scrollTop) {
            currentScrollDirection.value = 'down';
          } else {
            currentScrollDirection.value = 'up';
          }
          currentScrollTop.value = parentScrollContainer.scrollTop;

          if (entries[0].intersectionRatio < 1 && currentScrollDirection.value === 'up' && !entries[0].isIntersecting) {
            emit('changeShowStickyBgTop', true);
          } else {
            emit('changeShowStickyBgTop', false);
          }

          if (
            entries[0].rootBounds &&
            ((entries[0].intersectionRect.top === 0 &&
              !entries[0].isIntersecting &&
              currentScrollDirection.value === '' &&
              entries[0].intersectionRatio === 0) ||
              (entries[0].intersectionRatio < 1 &&
                currentScrollDirection.value === 'down' &&
                !entries[0].isIntersecting))
          ) {
            emit('changeShowStickyBgBottom', true);
          } else {
            emit('changeShowStickyBgBottom', false);
          }
        },
        {
          threshold: [0.25, 0.75, 1],
          root: el.value?.parentNode?.parentNode as HTMLElement,
          rootMargin: '0px 0px -20px 0px'
        }
      );

      intersectionObserver.value.observe(actualElement as HTMLElement);
    }
  },
  { immediate: true }
);
</script>

<style lang="scss" scoped>
.rewards {
  display: flex;
  gap: 16px;
  height: 34px;
  min-height: 34px;
  flex-direction: row-reverse;
  border-radius: 12px;
  padding: 2px 16px;
  background: rgb(7, 24, 33);
  background: linear-gradient(90deg, rgba(7, 24, 33, 1) 0%, rgba(4, 14, 19, 1) 35%);

  .chest {
    order: 1;
    width: 28px;
    max-width: 28px;
    max-height: 28px;
  }

  .reward-item {
    display: flex;
    align-items: center;
    gap: 4px;

    span {
      line-height: 20px;
      font-size: 20px;
      font-weight: 500;
      color: white;
      opacity: 0.5;
      z-index: 0;
    }

    img {
      height: 24px;
      min-height: 24px;
      min-width: 24px;
    }
  }
}

.leaderboardItem {
  height: 40px;
  border: 1px solid #3b7285;
  border-radius: 12px;
  padding: 0 0 0 24px;
  background: linear-gradient(to bottom, #17343e 0%, #010303 100%);
  display: flex;
  align-items: center;
  justify-content: space-between;

  overflow: hidden;

  @media screen and (max-width: 767px) {
    padding: 0 0 0 12px;
    min-width: 600px;
    width: 100%;
    flex-shrink: 0;
  }

  &.ownPositionBG {
    background: linear-gradient(to bottom, #0083ad 0%, #00354f 100%);

    @media (hover: hover) {
      &:hover {
        cursor: pointer;
        background: radial-gradient(120% 100% at 50% 100%, rgba(0, 131, 173, 1), rgba(0, 0, 0, 0) 50%),
          linear-gradient(to bottom, #17343e 0%, #010303 100%);
      }
    }

    .reward-item span {
      opacity: 1;
    }
  }

  .score,
  .address {
    display: flex;
    align-items: center;
    margin: 0;
  }

  .score.isJackpot {
    background-color: #000000b2;
    border-left: 1px solid #2b3c42;
    padding: 0 24px;
    height: 100%;
  }

  .address {
    white-space: nowrap;
    flex-shrink: 0;
    min-width: fit-content;
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 0 0 auto;

    span {
      color: #90989d;
      font-size: 16px;
      font-family: Eczar, sans-serif;
      white-space: nowrap;
      flex-shrink: 0;

      &:first-child {
        color: #eea92e;
        font-size: 16px;
        width: 45px;
        flex-shrink: 0;
      }
    }

    .user-name {
      &.ownPositionRow span {
        color: #fff;
      }
    }

    .user-name {
      flex-shrink: 0;
      min-width: fit-content;

      span {
        color: #90989d;
        margin-left: 0;
        white-space: nowrap;
        overflow: visible;
        text-overflow: clip;
        max-width: none;

        @media screen and (max-width: 767px) {
          max-width: none;
        }
      }
    }
  }

  .score {
    white-space: nowrap;
    flex-shrink: 0;

    .season-points {
      display: flex;
      align-items: center;
      color: #eea92e;
      font-size: 22px;
      gap: 5px;
      border-right: 1px solid #2b3c42;
      padding-right: 24px;
      white-space: nowrap;

      @media screen and (max-width: 560px) {
        font-size: 20px;
      }

      &:last-child {
        margin-left: 15px;
      }

      span {
        white-space: nowrap;
      }
    }
  }
}

.own-background {
  position: absolute;
  top: -10px;
  left: 0;
  width: 100%;
  height: 60px;
  background: #040e13;
  z-index: -1;
}

.prize {
  display: flex;
  align-items: center;
  gap: 5px;
  width: 180px;
  white-space: nowrap;
  flex-shrink: 0;
  min-width: fit-content;

  .pearl-reward {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 22px;
    margin-left: 5px;

    @media screen and (max-width: 560px) {
      font-size: 20px;
    }

    span {
      color: #fff;
      font-size: 20px;
    }
  }

  .no-rewards-text {
    color: #90989d;
    font-size: 16px;
    font-family: Eczar, sans-serif;
    opacity: 0.5;
    margin: 0 auto;
    padding-left: 24px;
  }
}
</style>
